import { useState, useMemo } from 'react';
import { Search, Plus, Pencil, History, X } from 'lucide-react';
import { useReactTable, getCoreRowModel, type ColumnDef } from '@tanstack/react-table';
import { useCustomers, type Customer, type CustomerSortBy } from '@/hooks/customer-engagement/use-customers';
import { useCustomerHistory } from '@/hooks/use-pos-reports';
import { formatCurrency, formatDateTime } from '@/lib/utils';
import { getReportingTimeZone } from '@/lib/report-timezone';
import { apiErrorMessage } from '@/lib/api-error';
import { DataTable } from '@/components/data-table/data-table';
import { SortHeader } from '@/components/data-table/sort-header';
import { DataTablePagination } from '@/components/data-table/data-table-pagination';
import { CreateCustomerDialog } from './create-customer-dialog';
import { customerTypeLabel } from './customer-type-label';
import { SearchableSelect } from '@/components/ui/searchable-select';
import { usePermissions } from '@/hooks/use-permissions';

type CustomerTypeFilter = '' | 'retail' | 'credit' | 'wholesale';

const CUSTOMER_TYPE_OPTIONS: { value: Exclude<CustomerTypeFilter, ''>; label: string }[] = [
  { value: 'retail', label: 'Retail' },
  { value: 'credit', label: 'Credit' },
  { value: 'wholesale', label: 'Wholesale' },
];

const PER_PAGE_OPTIONS = [10, 25, 50, 100];

// Height the pinned pagination bar occupies under the table, plus the gap
// above it — the table's max-height is computed to leave exactly this free.
// Same value as All Products, which uses the same bar.
const PAGINATION_BAR_HEIGHT = 86;

export function CustomerListSection() {
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(25);
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState<CustomerTypeFilter>('');
  // Server-side sort on the numeric columns; unset = most recently updated first.
  const [sortBy, setSortBy] = useState<CustomerSortBy | undefined>(undefined);
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');
  const [dialog, setDialog] = useState<{ mode: 'create' } | { mode: 'edit'; customer: Customer } | null>(null);
  const [historyCustomer, setHistoryCustomer] = useState<Customer | null>(null);
  const permissions = usePermissions();
  const canCreate = permissions.has('customers.create');
  const canEdit = permissions.has('customers.edit');

  const params = useMemo(
    () => ({
      search: search.trim() || undefined,
      customerType: typeFilter || undefined,
      limit: perPage,
      offset: (page - 1) * perPage,
      sortBy,
      sortDir,
    }),
    [search, typeFilter, page, perPage, sortBy, sortDir],
  );

  const handleSort = (field: CustomerSortBy) => {
    if (sortBy === field) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortBy(field);
      setSortDir('desc');
    }
    setPage(1);
  };
  const sortHeader = (label: string, field: CustomerSortBy) => (
    <div className="text-right">
      <SortHeader label={label} field={field} currentSort={sortBy} currentDir={sortDir} onSort={handleSort} />
    </div>
  );

  const { data, isLoading, isError, error } = useCustomers(params);

  const customers = data?.data ?? [];
  const total = data?.meta.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / perPage));

  const handlePerPageChange = (newPerPage: number) => {
    setPerPage(newPerPage);
    setPage(1);
  };

  const columns: ColumnDef<Customer>[] = [
    {
      accessorKey: 'name',
      header: 'Name',
      cell: ({ row }) => (
        <div>
          <div className="font-medium text-foreground">{row.original.name || '—'}</div>
          {row.original.businessName && (
            <div className="text-xs text-muted-foreground">{row.original.businessName}</div>
          )}
        </div>
      ),
    },
    {
      accessorKey: 'phone',
      header: 'Phone',
      cell: ({ row }) => <span className="text-sm">{row.original.phone || '—'}</span>,
    },
    {
      accessorKey: 'email',
      header: 'Email',
      cell: ({ row }) =>
        row.original.email ? (
          <span className="text-sm">{row.original.email}</span>
        ) : (
          <span className="text-sm text-muted-foreground">—</span>
        ),
    },
    {
      accessorKey: 'customerType',
      header: 'Type',
      cell: ({ row }) => {
        const badge =
          row.original.customerType === 'credit'
            ? 'bg-amber-100 text-amber-800'
            : row.original.customerType === 'wholesale'
              ? 'bg-purple-100 text-purple-800'
              : 'bg-gray-100 text-gray-700';
        return (
          <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${badge}`}>
            {customerTypeLabel(row.original.customerType)}
          </span>
        );
      },
    },
    {
      accessorKey: 'loyaltyPoints',
      header: () => sortHeader('Loyalty pts', 'loyaltyPoints'),
      cell: ({ row }) => <div className="text-right">{row.original.loyaltyPoints.toLocaleString()}</div>,
    },
    {
      accessorKey: 'currentBalance',
      header: () => sortHeader('Balance', 'currentBalance'),
      cell: ({ row }) => <div className="text-right">{formatCurrency(Number(row.original.currentBalance))}</div>,
    },
    {
      accessorKey: 'creditLimit',
      header: () => sortHeader('Credit limit', 'creditLimit'),
      cell: ({ row }) => <div className="text-right">{formatCurrency(Number(row.original.creditLimit))}</div>,
    },
    {
      accessorKey: 'totalVisits',
      header: () => <div className="text-right">Visits</div>,
      cell: ({ row }) => <div className="text-right tabular-nums">{row.original.totalVisits}</div>,
    },
    {
      accessorKey: 'lastVisitAt',
      header: 'Last visit',
      cell: ({ row }) =>
        row.original.lastVisitAt ? (
          <span className="text-sm text-muted-foreground whitespace-nowrap">
            {formatDateTime(row.original.lastVisitAt)}
          </span>
        ) : (
          <span className="text-sm text-muted-foreground">never</span>
        ),
    },
    {
      // Where the customer originally signed up, with the date underneath.
      // Older customers whose location was never recorded show "Unknown".
      accessorKey: 'signupLocationName',
      header: 'Signup location',
      cell: ({ row }) => (
        <div className="whitespace-nowrap">
          <div className={row.original.signupLocationName ? 'text-sm' : 'text-sm text-muted-foreground'}>
            {row.original.signupLocationName || 'Unknown'}
          </div>
          {row.original.signupDate && (
            <div className="text-xs text-muted-foreground">
              {/* signup_date is a timestamp; show its calendar day in the
                  reporting zone, as formatDateTime does, so it can't shift a day. */}
              {new Date(row.original.signupDate).toLocaleDateString(undefined, { timeZone: getReportingTimeZone() })}
            </div>
          )}
        </div>
      ),
    },
    {
      id: 'actions',
      header: () => <div className="text-right">Actions</div>,
      // Pin to the right so the row actions stay reachable while the table
      // scrolls horizontally, as on All Products. The shared DataTable only
      // applies the pin from the `sm` breakpoint up, so on a phone this is an
      // ordinary trailing column.
      meta: { sticky: 'right' },
      cell: ({ row }) => (
        <div className="flex items-center justify-end gap-1">
          <button
            type="button"
            onClick={() => setHistoryCustomer(row.original)}
            className="inline-flex h-8 w-8 items-center justify-center rounded-md border border-border hover:bg-muted"
            title="View history"
            aria-label="View history"
          >
            <History className="h-4 w-4 text-primary" />
          </button>
          {canEdit && (
            <button
              type="button"
              onClick={() => setDialog({ mode: 'edit', customer: row.original })}
              className="inline-flex h-8 w-8 items-center justify-center rounded-md border border-border hover:bg-muted"
              title="Edit customer"
              aria-label="Edit customer"
            >
              <Pencil className="h-4 w-4 text-primary" />
            </button>
          )}
        </div>
      ),
    },
  ];

  const table = useReactTable({
    data: customers,
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  return (
    <div className="space-y-4">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h2 className="text-xl font-semibold text-gray-900">Customers</h2>
          <p className="text-sm text-gray-600 mt-1">
            Customers enrolled at the register or added from the dashboard. Search by phone, name, or email.
          </p>
        </div>
        {canCreate && (
          <button
            type="button"
            onClick={() => setDialog({ mode: 'create' })}
            className="shrink-0 inline-flex items-center gap-2 rounded-lg bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90"
          >
            <Plus className="h-4 w-4" /> Create
          </button>
        )}
      </div>

      <CreateCustomerDialog
        open={!!dialog}
        customer={dialog?.mode === 'edit' ? dialog.customer : null}
        onClose={() => setDialog(null)}
      />

      {historyCustomer && <CustomerHistoryModal customer={historyCustomer} onClose={() => setHistoryCustomer(null)} />}

      <div className="flex flex-wrap items-center gap-3">
        <div className="relative w-full min-w-0 sm:flex-1 sm:w-auto sm:max-w-md">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <input
            type="search"
            placeholder="Search phone, name, or email..."
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(1);
            }}
            className="h-9 w-full rounded-lg border border-input bg-background pl-9 pr-4 text-sm outline-none focus:border-primary focus:ring-1 focus:ring-primary"
          />
        </div>
        <SearchableSelect
          className="w-44"
          value={typeFilter}
          onChange={(next) => {
            setTypeFilter(next as CustomerTypeFilter);
            setPage(1);
          }}
          options={CUSTOMER_TYPE_OPTIONS}
          placeholder="All types"
          searchPlaceholder="Search types..."
          emptyMessage="No matching type"
        />
      </div>

      {isError && (
        <div className="rounded-md bg-red-50 border border-red-200 p-3 text-sm text-red-700">
          Failed to load customers: {apiErrorMessage(error)}
        </div>
      )}

      {isLoading ? (
        <div className="flex h-64 items-center justify-center text-muted-foreground">Loading...</div>
      ) : (
        !isError && (
          <>
            {/* As on All Products: the table scrolls inside itself rather than
                stretching the page, so the header stays frozen and the
                horizontal scrollbar sits at the bottom of the table instead of
                the bottom of a very long page. The pagination bar is pinned to
                the bottom of the viewport. From md up this column sits beside
                the section nav, so the bar only bleeds out to the right. */}
            <DataTable table={table} columns={columns} fillViewport={{ reserve: PAGINATION_BAR_HEIGHT }} />
            <div className="sticky -bottom-4 z-30 -mx-4 border-t border-border bg-white px-4 shadow-[0_-2px_8px_rgba(15,23,42,0.06)] md:ml-0 md:pl-0 lg:-bottom-6 lg:-mr-6 lg:pr-6">
              <DataTablePagination
                page={page}
                totalPages={totalPages}
                total={total}
                perPage={perPage}
                onPageChange={setPage}
                perPageOptions={PER_PAGE_OPTIONS}
                onPerPageChange={handlePerPageChange}
              />
            </div>
          </>
        )
      )}
    </div>
  );
}

function CustomerHistoryModal({ customer, onClose }: { customer: Customer; onClose: () => void }) {
  const { data, isLoading } = useCustomerHistory(customer.id);
  const purchases = data?.data.purchases ?? [];
  const rewards = data?.data.rewards ?? [];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={onClose}>
      <div
        className="max-h-[85vh] w-full max-w-2xl overflow-y-auto rounded-lg border border-border bg-card p-5 shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mb-4 flex items-start justify-between">
          <div>
            <div className="text-lg font-semibold text-foreground">{customer.name || customer.phone}</div>
            <div className="text-sm text-muted-foreground">{customer.phone}</div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="inline-flex h-8 w-8 items-center justify-center rounded-md border border-border hover:bg-muted"
            aria-label="Close"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="mb-5 grid grid-cols-3 gap-3">
          <div className="rounded-md border border-border p-3">
            <div className="text-xs text-muted-foreground">Last visit</div>
            <div className="text-sm font-medium text-foreground">
              {customer.lastVisitAt ? formatDateTime(customer.lastVisitAt) : '—'}
            </div>
          </div>
          <div className="rounded-md border border-border p-3">
            <div className="text-xs text-muted-foreground">Total visits</div>
            <div className="text-sm font-medium text-foreground">{customer.totalVisits}</div>
          </div>
          <div className="rounded-md border border-border p-3">
            <div className="text-xs text-muted-foreground">Loyalty points</div>
            <div className="text-sm font-medium text-foreground">{customer.loyaltyPoints.toLocaleString()}</div>
          </div>
        </div>

        <div className="mb-5">
          <div className="mb-2 text-sm font-semibold text-foreground">Purchases</div>
          {isLoading ? (
            <div className="text-sm text-muted-foreground">Loading…</div>
          ) : purchases.length === 0 ? (
            <div className="text-sm text-muted-foreground">No purchases yet.</div>
          ) : (
            <div className="divide-y divide-border rounded-md border border-border">
              {purchases.map((p) => (
                <div key={p.id} className="flex items-center justify-between px-3 py-2 text-sm">
                  <span className="text-muted-foreground">
                    #{p.dailyOrderNumber ?? p.id.slice(0, 8)} · {formatDateTime(p.createdAt)}
                  </span>
                  <span className="font-mono">
                    ${formatCurrency((parseFloat(p.total) + parseFloat(p.tipAmount ?? '0')).toFixed(2))}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>

        <div>
          <div className="mb-2 text-sm font-semibold text-foreground">Points activity</div>
          {isLoading ? (
            <div className="text-sm text-muted-foreground">Loading…</div>
          ) : rewards.length === 0 ? (
            <div className="text-sm text-muted-foreground">No redeems or earns yet.</div>
          ) : (
            <div className="divide-y divide-border rounded-md border border-border">
              {rewards.map((r) => (
                <div key={r.id} className="flex items-center justify-between px-3 py-2 text-sm">
                  <span className="text-muted-foreground">
                    <span className={r.eventType === 'redeem' ? 'text-amber-600' : 'text-green-600'}>
                      {r.eventType === 'redeem' ? 'Redeem' : 'Earn'}
                    </span>{' '}
                    · {formatDateTime(r.createdAt)}
                    {parseFloat(r.discountApplied ?? '0') > 0
                      ? ` · $${formatCurrency(parseFloat(r.discountApplied ?? '0').toFixed(2))} off`
                      : ''}
                  </span>
                  <span className={`font-mono ${r.pointsDelta < 0 ? 'text-amber-600' : 'text-green-600'}`}>
                    {r.pointsDelta > 0 ? '+' : ''}
                    {r.pointsDelta} pts
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
